import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  useToast,
  Badge,
  HStack,
  Text,
  Switch,
  VStack,
  Select,
  Checkbox,
  TableContainer,
  Tabs,
  TabList,
  Tab,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiTag } from 'react-icons/fi';
import axios from 'axios';
import ConfirmModal from '../components/ConformModel/ConfirmModal';

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState(null);
  const [coinPackages, setCoinPackages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isDeletingCategory, setIsDeletingCategory] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState('');
  const [activeOfferTab, setActiveOfferTab] = useState('all');
  const [offerTiming, setOfferTiming] = useState({ dayStart: '06:00', dayEnd: '17:59', nightStart: '18:00', nightEnd: '05:59' });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isTimingOpen, onOpen: onTimingOpen, onClose: onTimingClose } = useDisclosure();
  const toast = useToast();
  const offerAvailabilitySelectStyles = {
    bg: 'var(--bg-input)',
    color: 'var(--text-primary)',
    borderColor: 'var(--border-default)',
    _hover: { borderColor: 'var(--border-strong)' },
    _focus: { borderColor: 'var(--primary-solid)', boxShadow: '0 0 0 3px rgba(168, 85, 247, 0.18)' },
    sx: { '& > option': { background: 'var(--bg-elevated)', color: 'var(--text-primary)' } },
  };

  const adminUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("adminUser") || "{}");
    } catch {
      return {};
    }
  }, []);

  const canManageCoupons = useMemo(() => {
    const allowedEmails = ["admin@arrowcoders.com", "haravindh@arrowcoders.com"];
    return allowedEmails.includes(adminUser?.email?.toLowerCase());
  }, [adminUser]);

  const [formData, setFormData] = useState({
    code: '',
    description: '',
    category: '',
    discountType: 'percentage', // 'percentage' or 'fixed'
    discountValue: '',
    minPurchase: '',
    maxPurchase: '',
    maxDiscount: '',
    expiryDate: '',
    usageLimit: '',
    usability: 'multiple', // 'one-time' | 'multiple'
    usageLimitType: 'unlimited', // 'count' | 'unlimited' (used when usability === 'multiple')
    active: true,
    platforms: {
      android: true,
      ios: true,
      web: true,
    },
    selectedPackages: [], // Array of selected coin package IDs
    offerType: 'all',
  });
  const [formErrors, setFormErrors] = useState({});

  const fetchCoupons = async (offerType = activeOfferTab) => {
    try {
      setLoading(true);
      const url = offerType === 'all'
        ? '/api/v2/admin/coupons'
        : `/api/v2/admin/coupons/offers/${offerType === 'day' ? 'daytime' : 'nighttime'}`;
      const res = await axios.get(url);
      if (res.data.success) {
        const list = res.data.data || [];
        setCoupons(list);
        // Best-effort categories list derived from existing coupons (supports string or {name})
        const extracted = list
          .map((c) => {
            const cat = c?.category;
            if (!cat) return null;
            if (typeof cat === 'string') return cat;
            if (typeof cat === 'object' && typeof cat.name === 'string') return cat.name;
            return null;
          })
          .filter(Boolean);
        setCategories(Array.from(new Set(extracted)));
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
      // Fallback to empty array if API fails
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOfferTiming = async () => {
    try {
      const res = await axios.get('/api/v2/admin/coupons/offer-timings');
      if (res.data.success) setOfferTiming(res.data.data);
    } catch (error) {
      console.error('Error fetching offer timings:', error);
    }
  };


  const fetchCoinPackages = async () => {
  try {
    const res = await axios.get('/api/v1/app/coin-packages');

    const packages = res.data.data?.packages || [];

    console.log("All Packages:", packages);

    const activePackages = packages.filter(pkg => pkg.isActive === true);

    console.log("Filtered Packages:", activePackages);

    setCoinPackages(activePackages);
  } catch (err) {
    console.log(err);
  }
};



  useEffect(() => {
    fetchCoupons();
    fetchCoinPackages();
    fetchOfferTiming();
  }, []);

  const handleOfferTabChange = (index) => {
    const offerType = ['all', 'day', 'night'][index];
    setActiveOfferTab(offerType);
    fetchCoupons(offerType);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSwitchChange = (e) => {
    setFormData({ ...formData, active: e.target.checked });
  };

  const handlePlatformChange = (platform, checked) => {
    setFormData({
      ...formData,
      platforms: {
        ...formData.platforms,
        [platform]: checked,
      },
    });
  };


  const handleUsabilityChange = (value) => {
    setFormData((prev) => {
      if (value === 'one-time') {
        return {
          ...prev,
          usability: 'one-time',
          usageLimitType: 'count',
          usageLimit: 1,
        };
      }
      // switching back to multiple
      const nextUsageLimitType =
        prev.usageLimitType === 'count' && prev.usageLimit !== '' && prev.usageLimit !== null
          ? 'count'
          : 'unlimited';
      const nextUsageLimit = nextUsageLimitType === 'count' ? prev.usageLimit : '';
      return {
        ...prev,
        usability: 'multiple',
        usageLimitType: nextUsageLimitType,
        usageLimit: nextUsageLimit,
      };
    });
  };

  const handleUsageLimitTypeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      usageLimitType: value,
      usageLimit: value === 'unlimited' ? '' : prev.usageLimit,
    }));
    if (value === 'unlimited' && formErrors.usageLimit) {
      setFormErrors((prev) => ({ ...prev, usageLimit: '' }));
    }
  };

  const handleAddCategory = () => {
    const name = newCategoryName.trim();
    if (!name) return;

    setCategories((prev) => {
      const exists = prev.some((c) => c.toLowerCase() === name.toLowerCase());
      return exists ? prev : [...prev, name].sort((a, b) => a.localeCompare(b));
    });

    setFormData((prev) => ({ ...prev, category: name }));
    setNewCategoryName('');
    setIsCreatingCategory(false);
  };

  const handleDeleteCategory = (categoryName) => {
    if (!categoryName) return;
    setCategories((prev) => prev.filter((c) => c !== categoryName));
    if (formData.category === categoryName) {
      setFormData((prev) => ({ ...prev, category: '' }));
    }
    setCategoryToDelete('');
    setIsDeletingCategory(false);
    toast({ title: 'Category removed', status: 'info', duration: 2000 });
  };

  const handlePackageSelectionChange = (packageId, checked) => {
    setFormData(prev => ({
      ...prev,
      selectedPackages: checked
        ? [...prev.selectedPackages, packageId]
        : prev.selectedPackages.filter(id => id !== packageId)
    }));
  };

  const handleAddCoupon = () => {
    setEditingCoupon(null);
    setIsCreatingCategory(false);
    setIsDeletingCategory(false);
    setNewCategoryName('');
    setCategoryToDelete('');
    setFormErrors({});
    setFormData({
      code: '',
      description: '',
      category: '',
      discountType: 'percentage',
      discountValue: '',
      minPurchase: '',
      maxPurchase: '',
      maxDiscount: '',
      expiryDate: '',
      usageLimit: '',
      usability: 'multiple',
      usageLimitType: 'unlimited',
      active: true,
      platforms: {
        android: true,
        ios: true,
        web: true,
      },
      selectedPackages: [],
      offerType: activeOfferTab,
    });
    onOpen();
  };



  const handleEditCoupon = (coupon) => {
  setEditingCoupon(coupon);
  setIsCreatingCategory(false);
  setIsDeletingCategory(false);
  setNewCategoryName('');
  setCategoryToDelete('');
  setFormErrors({});

  console.log("EDIT COUPON RAW DATA:", coupon);

  const categoryValue =
    typeof coupon.category === 'string'
      ? coupon.category
      : typeof coupon.category?.name === 'string'
        ? coupon.category.name
        : '';

  // ✅ FIX usage limit type
  const usageLimit =
    coupon.usageLimit === null || coupon.usageLimit === undefined
      ? ''
      : coupon.usageLimit;

  const inferredUsageLimitType =
    !usageLimit || usageLimit === 0 ? 'unlimited' : 'count';

  // ✅ FIX selectedPackages (ObjectId → string)
  const selectedPackages = (coupon.selectedPackages || []).map(id =>
    id.toString()
  );

  // ✅ FIX platforms fallback
  const platforms = coupon.platforms || {
    android: true,
    ios: true,
    web: true,
  };

  // ✅ FIX date format
  const expiryDate = coupon.expiryDate
    ? new Date(coupon.expiryDate).toISOString().split('T')[0]
    : '';

  // ✅ FIX numeric fields (convert to string for inputs)
  setFormData({
    code: coupon.code || '',
    description: coupon.description || '',
    category: categoryValue,
    discountType: coupon.discountType || 'percentage',
    discountValue: coupon.discountValue?.toString() || '',
    minPurchase: coupon.minPurchase?.toString() || '',
    maxPurchase: coupon.maxPurchase?.toString() || '',
    maxDiscount: coupon.maxDiscount?.toString() || '',
    expiryDate,
    usability: coupon.usability || 'multiple',
    usageLimitType: coupon.usability === 'one-time' ? 'count' : inferredUsageLimitType,
    usageLimit:
      coupon.usability === 'one-time'
        ? 1
        : inferredUsageLimitType === 'count'
          ? usageLimit.toString()
          : '',
    active: coupon.active ?? true,
    platforms,
    selectedPackages,
    offerType: coupon.offerType || 'all',
  });

  onOpen();
};



  const validateCouponForm = () => {
    const errors = {};

    if (!formData.code.trim()) errors.code = 'This field is required';
    if (!formData.discountType) errors.discountType = 'This field is required';
    if (formData.discountValue === '' || formData.discountValue === null) {
      errors.discountValue = 'This field is required';
    }
    if (formData.usability === 'multiple' && formData.usageLimitType === 'count' &&
      (formData.usageLimit === '' || formData.usageLimit === null)) {
      errors.usageLimit = 'This field is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateCouponForm()) return;

    try {
      const normalizedUsageLimit =
        formData.usability === 'one-time'
          ? 1
          : formData.usageLimitType === 'count'
            ? Number(formData.usageLimit || 0)
            : 0;

      const payload = {
        ...formData,
        usageLimit: normalizedUsageLimit,
        minPurchase: Number(formData.minPurchase || 0),
        maxPurchase: Number(formData.maxPurchase || 0),
        maxDiscount: Number(formData.maxDiscount || 0),
        discountValue: Number(formData.discountValue || 0),
        selectedPackages: formData.selectedPackages,
      };

      console.log("Sending Coupon Payload:", payload);

      if (editingCoupon) {
        const res = await axios.put(`/api/v2/admin/coupons/${editingCoupon._id}`, payload);
        if (res.data.success) {
          toast({ title: 'Coupon updated', status: 'success', duration: 3000 });
        }
      } else {
        const res = await axios.post('/api/v2/admin/coupons', payload);
        if (res.data.success) {
          toast({ title: 'Coupon created', status: 'success', duration: 3000 });
        }
      }

      fetchCoupons(activeOfferTab);
      onClose();
    } catch (error) {
      console.error('Error saving coupon:', error);
      toast({ title: 'Error saving coupon', status: 'error', duration: 3000 });
    }
  };

  const handleOfferTimingSave = async () => {
    try {
      const payload = {
        dayStart: offerTiming.dayStart,
        dayEnd: offerTiming.dayEnd,
        nightStart: offerTiming.nightStart,
        nightEnd: offerTiming.nightEnd,
      };
      const res = await axios.put('/api/v2/admin/coupons/offer-timings', payload);
      if (res.data.success) {
        setOfferTiming(res.data.data);
        toast({ title: 'Offer timings updated', status: 'success', duration: 3000 });
        onTimingClose();
      }
    } catch (error) {
      console.error('Error updating offer timings:', error);
      toast({ title: error.response?.data?.message || 'Error updating offer timings', status: 'error', duration: 3000 });
    }
  };



  const handleDeleteClick = (coupon) => {
    setCouponToDelete(coupon);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const res = await axios.delete(`/api/v2/admin/coupons/${couponToDelete._id}`);
      if (res.data.success) {
        toast({ title: 'Coupon deleted', status: 'success', duration: 3000 });
        fetchCoupons();
      }
    } catch (error) {
      console.error('Error deleting coupon:', error);
      toast({ title: 'Error deleting coupon', status: 'error', duration: 3000 });
    } finally {
      setDeleteConfirmOpen(false);
      setCouponToDelete(null);
    }
  };

  return (
    <Box p={6} bg="var(--bg-light)" minH="screen">
      <VStack align="stretch" spacing={6}>
        <Box
          display="flex"
          justifyContent="between"
          alignItems="center"
          bg="var(--surface)"
          p={6}
          rounded="xl"
          border="2px"
          borderColor="var(--border-light)"
          shadow="sm"
        >
          <Box>
            <HStack spacing={3}>
              <FiTag size={24} color="var(--primary-solid)" />
              <Text fontSize="2xl" fontWeight="bold" color="var(--primary-solid)">Coupon Management</Text>
            </HStack>
            <Text color="gray.500" mt={1}>Create and manage discount coupons for users</Text>
          </Box>
          {canManageCoupons && (
            <HStack ml="auto">
              <Button
                variant="outline"
                onClick={onTimingOpen}
                color="var(--text-primary)"
                borderColor="var(--border-strong)"
                bg="transparent"
                _hover={{ bg: 'var(--bg-surface-hover)', borderColor: 'var(--primary-solid)', color: 'var(--text-primary)' }}
              >
                Offer Timings
              </Button>
              <Button
                leftIcon={<FiPlus />}
                onClick={handleAddCoupon}
                bg="var(--primary-solid)"
                color="white"
                _hover={{ opacity: 0.9 }}
              >
                Add New Coupon
              </Button>
            </HStack>
          )}
        </Box>

        <Box bg="var(--surface)" rounded="xl" border="2px" borderColor="var(--border-light)" shadow="sm" overflow="hidden">
         <Tabs index={['all', 'day', 'night'].indexOf(activeOfferTab)} onChange={handleOfferTabChange} px={4} pt={4}>
           <TabList>
             <Tab>All-time coupons</Tab>
             <Tab>Daytime offers</Tab>
             <Tab>Night-time offers</Tab>
           </TabList>
         </Tabs>
         <TableContainer overflowX="auto">
          <Table variant="simple">
            <Thead bg="var(--bg-elevated)">
              <Tr>
                <Th>Code</Th>
                <Th>Category</Th>
                <Th>Discount</Th>
                <Th>Min Purchase</Th>
                <Th>Expiry</Th>
                <Th>Limit</Th>
                <Th>Applicable Packages</Th>
                <Th>Status</Th>
                <Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {coupons.length === 0 ? (
                <Tr>
                  <Td colSpan={9} textAlign="center" py={10}>
                    {loading ? 'Loading coupons...' : 'No coupons found'}
                  </Td>
                </Tr>
              ) : (
                coupons.map((coupon) => {
                  const applicablePackages = coupon.selectedPackages || [];
                  const applicablePackageNames = applicablePackages.length > 0
                    ? coinPackages
                      .filter(pkg => applicablePackages.some(id => id.toString() === pkg._id.toString()))
                      .map(pkg => pkg.name)
                      .join(', ')
                    : 'All Packages';

                  return (
                    <Tr
                      key={coupon._id}
                      color="var(--text-secondary)"
                      _hover={{ bg: 'var(--bg-surface-hover)', color: 'var(--text-primary)' }}
                    >
                      <Td fontWeight="bold">{coupon.code}</Td>
                      <Td>
                        {typeof coupon.category === 'string'
                          ? coupon.category
                          : typeof coupon.category?.name === 'string'
                            ? coupon.category.name
                            : '—'}
                      </Td>
                      <Td>
                        {coupon.discountType === 'percentage'
                          ? `${coupon.discountValue}%`
                          : `₹${coupon.discountValue}`}
                      </Td>
                      <Td>₹{coupon.minPurchase || '0'}</Td>
                      <Td>{coupon.expiryDate ? new Date(coupon.expiryDate).toLocaleDateString() : 'No expiry'}</Td>
                      <Td>{coupon.usageLimit || 'Unlimited'}</Td>
                      <Td>
                        <Text fontSize="sm" title={applicablePackageNames}>
                          {applicablePackageNames.length > 20
                            ? `${applicablePackageNames.substring(0, 20)}...`
                            : applicablePackageNames}
                        </Text>
                      </Td>
                      <Td>
                        <Badge colorScheme={coupon.active ? 'green' : 'red'} rounded="full" px={3}>
                          {coupon.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </Td>
                      <Td textAlign="right">
                        {canManageCoupons && (
                          <HStack spacing={2} justify="end">
                            <IconButton
                              aria-label="Edit"
                              icon={<FiEdit2 />}
                              size="sm"
                              variant="ghost"
                              color="blue.500"
                              onClick={() => handleEditCoupon(coupon)}
                            />
                            <IconButton
                              aria-label="Delete"
                              icon={<FiTrash2 />}
                              size="sm"
                              variant="ghost"
                              color="red.500"
                              onClick={() => handleDeleteClick(coupon)}
                            />
                          </HStack>
                        )}
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
          </TableContainer>
        </Box>
      </VStack>

      {/* Add/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent rounded="2xl">
          <ModalHeader borderBottom="1px" borderColor="gray.100">
            {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={4}>
              <FormControl isRequired isInvalid={Boolean(formErrors.code)}>
                <FormLabel fontWeight="bold">Coupon Code</FormLabel>
                <Input
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="e.g. WELCOME50"
                  rounded="lg"
                />
                <FormErrorMessage>{formErrors.code}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="bold">Description</FormLabel>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Optional description for internal reference"
                  rounded="lg"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="bold">Offer availability</FormLabel>
                <Select
                  name="offerType"
                  value={formData.offerType}
                  onChange={handleInputChange}
                  rounded="lg"
                  {...offerAvailabilitySelectStyles}
                >
                  <option value="all">Any time</option>
                  <option value="day">Daytime offer</option>
                  <option value="night">Night-time offer</option>
                </Select>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Any time keeps this coupon available without an offer-time restriction.
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="bold">Category</FormLabel>
                <Select
                  value={isCreatingCategory ? '__create__' : isDeletingCategory ? '__delete__' : (formData.category || '')}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '__create__') {
                      setIsCreatingCategory(true);
                      setIsDeletingCategory(false);
                      setCategoryToDelete('');
                      return;
                    }
                    if (v === '__delete__') {
                      setIsDeletingCategory(true);
                      setIsCreatingCategory(false);
                      setFormData((prev) => ({ ...prev, category: '' }));
                      return;
                    }
                    setIsCreatingCategory(false);
                    setIsDeletingCategory(false);
                    setCategoryToDelete('');
                    setFormData((prev) => ({ ...prev, category: v }));
                  }}
                  rounded="lg"
                  {...offerAvailabilitySelectStyles}
                >
                  <option value="">Select category</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                  <option value="__create__">+ Create new category</option>
                  {categories.length > 0 && (
                    <option value="__delete__">— Delete a category —</option>
                  )}
                </Select>

                {isCreatingCategory && (
                  <HStack mt={2}>
                    <Input
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="New category name"
                      rounded="lg"
                    />
                    <Button onClick={handleAddCategory} bg="var(--primary-solid)" color="white" _hover={{ opacity: 0.9 }}>
                      Add
                    </Button>
                  </HStack>
                )}

                {isDeletingCategory && categories.length > 0 && (
                  <HStack mt={2} spacing={2} alignItems="flex-end">
                    <FormControl flex={1}>
                      <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">Category to delete</FormLabel>
                      <Select
                        value={categoryToDelete}
                        onChange={(e) => setCategoryToDelete(e.target.value)}
                        placeholder="Choose category"
                        rounded="lg"
                        {...offerAvailabilitySelectStyles}
                      >
                        {categories.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      leftIcon={<FiTrash2 />}
                      colorScheme="red"
                      variant="outline"
                      isDisabled={!categoryToDelete}
                      onClick={() => handleDeleteCategory(categoryToDelete)}
                    >
                      Delete
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setIsDeletingCategory(false); setCategoryToDelete(''); }}>
                      Cancel
                    </Button>
                  </HStack>
                )}
              </FormControl>

              <HStack w="full" spacing={4}>
                <FormControl isRequired isInvalid={Boolean(formErrors.discountType)}>
                  <FormLabel fontWeight="bold">Discount Type</FormLabel>
                  <Select name="discountType" value={formData.discountType} onChange={handleInputChange} rounded="lg" {...offerAvailabilitySelectStyles}>
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </Select>
                  <FormErrorMessage>{formErrors.discountType}</FormErrorMessage>
                </FormControl>
                <FormControl isRequired isInvalid={Boolean(formErrors.discountValue)}>
                  <FormLabel fontWeight="bold">Discount Value</FormLabel>
                  <Input
                    name="discountValue"
                    type="number"
                    value={formData.discountValue}
                    onChange={handleInputChange}
                    placeholder="Value"
                    rounded="lg"
                  />
                  <FormErrorMessage>{formErrors.discountValue}</FormErrorMessage>
                </FormControl>
              </HStack>

              <HStack w="full" spacing={4}>
                <FormControl>
                  <FormLabel fontWeight="bold">Min Purchase (₹)</FormLabel>
                  <Input
                    name="minPurchase"
                    type="number"
                    value={formData.minPurchase}
                    onChange={handleInputChange}
                    placeholder="Minimum amount"
                    rounded="lg"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontWeight="bold">Max Purchase (₹)</FormLabel>
                  <Input
                    name="maxPurchase"
                    type="number"
                    value={formData.maxPurchase}
                    onChange={handleInputChange}
                    placeholder="Maximum amount"
                    rounded="lg"
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontWeight="bold">Max Discount (₹)</FormLabel>
                <Input
                  name="maxDiscount"
                  type="number"
                  value={formData.maxDiscount}
                  onChange={handleInputChange}
                  placeholder="Maximum discount"
                  rounded="lg"
                />
              </FormControl>

              <HStack w="full" spacing={4}>
                <FormControl>
                  <FormLabel fontWeight="bold">Expiry Date</FormLabel>
                  <Input
                  sx={{
  "&::-webkit-calendar-picker-indicator": {
    filter: "brightness(0) invert(1)",
  },
}}
                    name="expiryDate"
                    type="date"
                    value={formData.expiryDate}
                    onChange={handleInputChange}
                    rounded="lg"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontWeight="bold">Usability</FormLabel>
                  <Select
                    value={formData.usability}
                    onChange={(e) => handleUsabilityChange(e.target.value)}
                    rounded="lg"
                    {...offerAvailabilitySelectStyles}
                  >
                    <option value="one-time">One time</option>
                    <option value="multiple">Multiple times</option>
                  </Select>
                </FormControl>
              </HStack>

              {formData.usability === 'multiple' && (
                <HStack w="full" spacing={4}>
                  <FormControl>
                    <FormLabel fontWeight="bold">Usage Count</FormLabel>
                    <Select
                      value={formData.usageLimitType}
                      onChange={(e) => handleUsageLimitTypeChange(e.target.value)}
                      rounded="lg"
                      {...offerAvailabilitySelectStyles}
                    >
                      <option value="unlimited">Unlimited</option>
                      <option value="count">Set count</option>
                    </Select>
                  </FormControl>
                  <FormControl
                    isRequired={formData.usageLimitType === 'count'}
                    isDisabled={formData.usageLimitType !== 'count'}
                    isInvalid={Boolean(formErrors.usageLimit)}
                  >
                    <FormLabel fontWeight="bold">Count</FormLabel>
                    <Input
                      name="usageLimit"
                      type="number"
                      min={1}
                      value={formData.usageLimitType === 'count' ? formData.usageLimit : ''}
                      onChange={handleInputChange}
                      placeholder="e.g. 100"
                      rounded="lg"
                    />
                    <FormErrorMessage>{formErrors.usageLimit}</FormErrorMessage>
                  </FormControl>
                </HStack>
              )}

              <HStack spacing="8" alignItems="flex-start">
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0" fontWeight="bold">Active Status</FormLabel>
                  <Switch
                    isChecked={formData.active}
                    onChange={handleSwitchChange}
                    colorScheme="pink"
                  />
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <FormLabel fontWeight="bold" mb="0" mr="4">Platform Availability</FormLabel>
                  <HStack spacing="6">
                    <Checkbox
                      isChecked={formData.platforms.android}
                      onChange={(e) => handlePlatformChange('android', e.target.checked)}
                      colorScheme="pink"
                    >
                      Android
                    </Checkbox>
                    <Checkbox
                      isChecked={formData.platforms.ios}
                      onChange={(e) => handlePlatformChange('ios', e.target.checked)}
                      colorScheme="pink"
                    >
                      iOS
                    </Checkbox>
                    <Checkbox
                      isChecked={formData.platforms.web}
                      onChange={(e) => handlePlatformChange('web', e.target.checked)}
                      colorScheme="pink"
                    >
                      web
                    </Checkbox>
                  </HStack>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontWeight="bold">Applicable Coin Packages</FormLabel>
                <Text fontSize="sm" color="gray.600" mb={2}>
                  Select the coin packages this coupon applies to. Leave empty to apply to all packages.
                </Text>
                <Box maxH="200px" overflowY="auto" border="1px" borderColor="gray.200" rounded="md" p={2}>
                  {coinPackages.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">Loading coin packages...</Text>
                  ) : (
                    <VStack align="start" spacing={2}>
                      {coinPackages.map((pkg) => (
                        <Checkbox
                          key={pkg._id || pkg.id}
                          isChecked={formData.selectedPackages.includes(pkg._id || pkg.id)}
                          onChange={(e) => handlePackageSelectionChange(pkg._id || pkg.id, e.target.checked)}
                          colorScheme="pink"
                        >
                          <HStack>
                            <Text fontSize="sm" fontWeight="medium">{pkg.name}</Text>
                            <Text fontSize="xs" color="gray.500">({pkg.coins} coins)</Text>
                          </HStack>
                        </Checkbox>
                      ))}
                    </VStack>
                  )}
                </Box>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter borderTop="1px" borderColor="gray.100">
            <Button
              variant="outline"
              mr={3}
              onClick={onClose}
              color="var(--text-primary)"
              borderColor="var(--border-strong)"
              bg="transparent"
              _hover={{ bg: 'var(--bg-surface-hover)', borderColor: 'var(--primary-solid)' }}
            >
              Cancel
            </Button>
            <Button
              bg="var(--primary-solid)"
              color="white"
              _hover={{ opacity: 0.9 }}
              onClick={handleSubmit}
            >
              {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isTimingOpen} onClose={onTimingClose} size="md">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent rounded="2xl">
          <ModalHeader>Offer Timings</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={4}>Times are evaluated in India Standard Time (Asia/Kolkata).</Text>
            <VStack spacing={4}>
              <HStack w="full">
                <FormControl><FormLabel>Day starts</FormLabel><Input sx={{
  "&::-webkit-calendar-picker-indicator": {
    filter: "brightness(0) invert(1)",
  },
}} type="time" value={offerTiming.dayStart} onChange={(e) => setOfferTiming({ ...offerTiming, dayStart: e.target.value })} /></FormControl>
                <FormControl><FormLabel>Day ends</FormLabel><Input sx={{
  "&::-webkit-calendar-picker-indicator": {
    filter: "brightness(0) invert(1)",
  },
}} type="time" value={offerTiming.dayEnd} onChange={(e) => setOfferTiming({ ...offerTiming, dayEnd: e.target.value })} /></FormControl>
              </HStack>
              <HStack w="full">
                <FormControl><FormLabel>Night starts</FormLabel><Input sx={{
  "&::-webkit-calendar-picker-indicator": {
    filter: "brightness(0) invert(1)",
  },
}} type="time" value={offerTiming.nightStart} onChange={(e) => setOfferTiming({ ...offerTiming, nightStart: e.target.value })} /></FormControl>
                <FormControl><FormLabel>Night ends</FormLabel><Input sx={{
  "&::-webkit-calendar-picker-indicator": {
    filter: "brightness(0) invert(1)",
  },
}} type="time" value={offerTiming.nightEnd} onChange={(e) => setOfferTiming({ ...offerTiming, nightEnd: e.target.value })} /></FormControl>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              mr={3}
              onClick={onTimingClose}
              color="var(--text-primary)"
              borderColor="var(--border-strong)"
              bg="transparent"
              _hover={{ bg: 'var(--bg-surface-hover)', borderColor: 'var(--primary-solid)' }}
            >
              Cancel
            </Button>
            <Button bg="var(--primary-solid)" color="white" _hover={{ opacity: 0.9 }} onClick={handleOfferTimingSave}>Save timings</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        title="Delete Coupon"
        message={`Are you sure you want to delete coupon? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </Box>
  );
};

export default Coupons;
